<script lang="ts" setup>
import type { MaterialSymbolName } from '@/types/materialSymbolNames';
import { useLabelColors } from '@/modules/labels/utils';

export type LabelSize = 'small' | 'medium' | 'large';
export type LabelColour =
  | 'default'
  | 'primary'
  | 'alert'
  | 'warning'
  | 'info'
  | 'purple'
  | 'critical';

withDefaults(
  defineProps<{
    colour?: LabelColour;
    size?: LabelSize;
    closeable?: boolean;
    filled?: boolean;
    icon?: MaterialSymbolName;
    hexColour?: string;
  }>(),
  {
    colour: 'default',
    size: 'small',
    filled: true,
    icon: undefined,
    hexColour: undefined,
  },
);

const emit = defineEmits<{
  close: [];
}>();

const { getBgColor, getBorderColor } = useLabelColors();
</script>

<template>
  <span
    class="label border-box inline-flex rounded-full border py-1x leading-[125%] whitespace-nowrap"
    :class="[
      colour,
      {
        'px-1.5x text-sm': size === 'small',
        'px-1.5x text-base': size === 'medium',
        'px-2x text-md': size === 'large',
        filled,
        closeable,
      },
    ]"
    :style="{
      backgroundColor: filled && hexColour ? getBgColor(hexColour) : undefined,
      borderColor: hexColour ? getBorderColor(hexColour, filled) : undefined,
      color: filled ? undefined : hexColour,
    }"
  >
    <BaseIcon v-if="icon" :size="size" :name="icon" :colour="colour" />
    <span
      class="flex flex-1 items-center font-medium"
      :class="{ 'pl-1.5x': closeable, 'px-1.5x': !closeable }"
    >
      <slot />
    </span>
    <button
      v-if="closeable"
      class="unstyled-button relative flex items-center pl-2x opacity-80"
      @click="emit('close')"
    >
      <BaseIcon :size="size" name="close" class="leading-none" data-testid="label-close" />
      <div class="absolute inset-0 -mx-2x -my-1x"></div>
    </button>
  </span>
</template>

<style scoped>
.label {
  &.default {
    color: var(--text-color-neutral);
    border-color: var(--border-color-neutral);

    &.filled {
      background-color: var(--background-color-neutral);
    }

    &.closeable:hover {
      background-color: var(--background-color-neutral-hover);
    }
  }

  &.primary {
    color: var(--text-color-primary);
    border-color: var(--border-color-primary);

    &.filled {
      background-color: var(--background-color-primary);
    }

    &.closeable:hover {
      background-color: var(--background-color-primary-hover);
    }
  }

  &.alert {
    color: var(--text-color-error);
    border-color: var(--border-color-error);

    &.filled {
      background-color: var(--background-color-error);
    }

    &.closeable:hover {
      background-color: var(--background-color-error-hover);
    }
  }

  &.critical {
    color: var(--text-color-white);
    border-color: var(--border-color-strong-error);

    &.filled {
      background-color: var(--background-color-strong-error-muted);
    }

    &.closeable:hover {
      background-color: var(--background-color-strong-error-hover);
    }
  }

  &.warning {
    color: var(--text-color-warning);
    border-color: var(--border-color-warning);

    &.filled {
      background-color: var(--background-color-warning);
    }

    &.closeable:hover {
      background-color: var(--background-color-warning-hover);
    }
  }

  &.info {
    color: var(--text-color-info);
    border-color: var(--border-color-info);

    &.filled {
      background-color: var(--background-color-info);
    }

    &.closeable:hover {
      background-color: var(--background-color-info-hover);
    }
  }

  &.purple {
    color: var(--text-color-community);
    border-color: var(--border-color-community);

    &.filled {
      background-color: var(--background-color-community);
    }

    &.closeable:hover {
      background-color: var(--background-color-community-hover);
    }
  }
}
</style>
